import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref, reactive, computed } from 'vue'
import { useTimestamp } from '@vueuse/core'
import {
  getMe,
  resetSystemToInitialState,
  getMiniStepperStatus,
  setMiniStepperStatusLayoutsVisited,
  getStepperStatus,
} from '@/api/auth'
import {
  getFields,
  getFieldsPerMultiMap,
  getFieldOperators,
  getCountryPicklistStates,
} from '@/api/metadata'
import { getRoles } from '@/api/roles'
import { useConnections } from './connections'
import { useStats } from './stats'
import { getSemanticTypes } from '../api/metadata'

export const useSession = defineStore('session', () => {
  const connectionsStore = useConnections()

  const isStartingPageLoading = ref(false)
  const reindexingInterval = 3 * 60000 // 3 min

  let systemStatusTimer = null
  let pendingMetadataReload = null
  let usesSystemMetadata = false

  const dedupeAgentId = ref(localStorage.getItem('dedupeAgentId'))
  const searchAgentId = ref(localStorage.getItem('searchAgentId'))
  const findFixAgentId = ref(localStorage.getItem('findFixAgentId'))
  // Not cached like the others: the agent only exists after a first Search &
  // Merge, and the Audit Log's Origin filter appears once it does.
  const searchMergeAgentId = ref(null)
  const importUrl = ref(null)

  const stepper = ref(JSON.parse(localStorage.getItem('stepper')))

  const enableXOBreakup = ref(false)
  const isAddressEnabled = ref(false)
  const isSlackIntegrationEnabled = ref(false)
  const user = ref(JSON.parse(localStorage.getItem('user')))
  // Seeded from the cached user so getSystemMetadata() doesn't race the
  // non-awaited getUser() refresh in the router guard
  const isSfCountryPicklistEnabled = ref(
    user.value?.isSfCountryPicklistEnabled || false,
  )
  // Data Issues license gate. Seeded from the cached user and defaulting to
  // enabled so the first paint before getUser() resolves matches the org's
  // last known entitlement instead of flashing the gated-off UI
  const isDataIssuesEnabled = ref(user.value?.isDataIssuesEnabled !== false)

  // Search & Merge feature flag (ENG-12644), on the /me payload alongside
  // isDataIssuesEnabled rather than its own settings call - the endpoint
  // requires settings.get, which the flag has nothing to do with. Unlike
  // isDataIssuesEnabled, this defaults to OFF: it is a feature flag rather
  // than a license entitlement, so it fails closed, and a nav entry appearing
  // a moment late is harmless where showing a gated feature is not.
  const canSearchMerge = ref(user.value?.canSearchMerge === true)
  // The flag alone. The screen is Admin-only, but the Audit Log shows
  // Search & Merge sets to every role that can open it.
  const searchMergeFlagEnabled = ref(
    user.value?.searchMergeFlagEnabled === true,
  )

  const herokuToken = ref(localStorage.getItem('herokuToken'))

  const systemStatus = ref(JSON.parse(localStorage.getItem('systemStatus')))

  const fieldsPerObject = ref(
    JSON.parse(localStorage.getItem('fieldsPerObject')),
  )
  const fieldsPerMultiMap = ref(
    JSON.parse(localStorage.getItem('fieldsPerMultiMap')),
  )
  const operators = ref(JSON.parse(localStorage.getItem('operators')))

  const contextMessage = ref(null)
  const timestamp = useTimestamp()
  const roles = ref(null)
  const jwt = ref(null)
  const isSessionExpired = ref(false)
  const lastRequestTimestamp = ref(null)
  const licenseUsage = ref(null)
  const isUserError = ref(false)
  const isUserFetched = ref(false)
  const isLoadingSystemStatus = ref(false)
  const isSystemStatusError = ref(false)
  const isLoadingMetadata = ref(false)
  const requesting = reactive({
    user: false,
    resetSystem: false,
    roles: false,
    stepper: false,
  })

  const isUserLoggedIn = computed(() => {
    return jwt.value || herokuToken.value
  })

  const isCdLite = computed(() => user.value?.license?.plan === 'cd_lite')

  const isUserInactive = computed(() => {
    return (
      isUserLoggedIn.value &&
      timestamp.value &&
      lastRequestTimestamp.value &&
      timestamp.value > lastRequestTimestamp.value
    )
  })

  function track(event) {
    if (contextMessage.value) {
      window.plausible(event, {
        props: { u: contextMessage.value.userId },
      })
    }
  }

  function setContextMessage(message) {
    contextMessage.value = message
    importUrl.value =
      window.location.origin +
      '/ci/action/import/entity/ui/?session=' +
      btoa(message.userName + '_session_' + message.sessionId)
  }

  async function getUser() {
    requesting.user = true
    isUserError.value = false

    try {
      const sessionData = await getMe()

      connectionsStore.setConnectionsList(sessionData.connections)

      enableXOBreakup.value = sessionData.enableXOBreakup
      isAddressEnabled.value = sessionData.addressEnabled || false
      isSlackIntegrationEnabled.value =
        sessionData.slackIntegrationEnabled || false
      isSfCountryPicklistEnabled.value =
        sessionData.sfCountryPicklistEnabled || false
      isDataIssuesEnabled.value = sessionData.dataIssuesEnabled !== false
      canSearchMerge.value = sessionData.canSearchMerge === true
      searchMergeFlagEnabled.value = sessionData.searchMergeFlagEnabled === true
      licenseUsage.value = sessionData.licenseUsage

      const userData = {
        id: sessionData.id,
        isSearchBeforeCreateEnabled: sessionData.isSearchBeforeCreateEnabled,
        isImportEnabled: sessionData.isImportEnabled,
        isAddressEnabled: sessionData.addressEnabled || false,
        isSlackIntegrationEnabled: sessionData.slackIntegrationEnabled || false,
        isSfCountryPicklistEnabled:
          sessionData.sfCountryPicklistEnabled || false,
        isDataIssuesEnabled: sessionData.dataIssuesEnabled !== false,
        canSearchMerge: sessionData.canSearchMerge === true,
        searchMergeFlagEnabled: sessionData.searchMergeFlagEnabled === true,
        license: sessionData.license,
      }

      user.value = userData
      localStorage.setItem('user', JSON.stringify(userData))

      systemStatus.value = sessionData.systemStatus
      localStorage.setItem(
        'systemStatus',
        JSON.stringify(sessionData.systemStatus),
      )

      if (systemStatus.value.isCrawlerReindexing) {
        reloadMetadataWhenReindexEnds()
        scheduleSystemStatusCheck()
      }

      stepper.value = sessionData.stepperStatus
      localStorage.setItem('stepper', JSON.stringify(sessionData.stepperStatus))

      dedupeAgentId.value = sessionData.agents.find(
        rule => rule.type === 'dedupe',
      )?.id
      searchAgentId.value = sessionData.agents.find(
        rule => rule.type === 'search',
      )?.id
      findFixAgentId.value = sessionData.agents.find(
        rule => rule.type === 'findfix',
      )?.id
      searchMergeAgentId.value =
        sessionData.agents.find(rule => rule.type === 'search_merge')?.id ??
        null

      localStorage.setItem('dedupeAgentId', dedupeAgentId.value)
      localStorage.setItem('searchAgentId', searchAgentId.value)
      localStorage.setItem('findFixAgentId', findFixAgentId.value)
    } catch (error) {
      isUserError.value = true
      throw error
    } finally {
      requesting.user = false
      isUserFetched.value = true
    }
  }

  // A first Search & Merge creates the agent the Audit Log's Origin filter
  // needs. Unlike getUser, a failure here doesn't raise the app-wide error:
  // Origin just stays off until the next page load.
  async function refreshSearchMergeAgent() {
    try {
      const sessionData = await getMe()
      searchMergeAgentId.value =
        sessionData.agents.find(rule => rule.type === 'search_merge')?.id ??
        null
    } catch {
      // Keep the agent id as it was.
    }
  }

  async function getStepper({ fullReload = true } = {}) {
    try {
      requesting.stepper = true
      const data = await getStepperStatus()

      if (fullReload) {
        stepper.value = data
      } else {
        stepper.value.isSalesforcePackageInstalled =
          data.isSalesforcePackageInstalled

        if (!stepper.value.otpKey) {
          stepper.value.otpKey = data.otpKey
        }
      }

      return stepper.value
    } finally {
      requesting.stepper = false
    }
  }

  const packageInstallUrl = computed(() => {
    if (connectionsStore.salesforceConnection.url.endsWith('/')) {
      return (
        connectionsStore.salesforceConnection.url.slice(0, -1) +
        stepper.value.packageInstallUrl
      )
    } else {
      return (
        connectionsStore.salesforceConnection.url +
        stepper.value.packageInstallUrl
      )
    }
  })

  async function getRolesList() {
    try {
      requesting.roles = true
      roles.value = await getRoles()
    } finally {
      requesting.roles = false
    }
  }

  // Fetches the roles once and shares the in-flight request between its own
  // callers. Resolves to false when the roles could not be loaded.
  let rolesRequest = null
  async function ensureRolesList() {
    if (roles.value) return true

    if (!rolesRequest) {
      rolesRequest = getRolesList()
        .then(() => true)
        .catch(() => false)
        .finally(() => (rolesRequest = null))
    }

    return rolesRequest
  }

  function setJWT(value) {
    jwt.value = value
  }

  function setSessionExpired() {
    isSessionExpired.value = true
    stopSystemStatusPolling()
  }

  async function setHerokuToken(value) {
    herokuToken.value = value
    localStorage.setItem('herokuToken', value)
  }

  function setLastRequestTimestamp() {
    const currentTime = new Date()
    currentTime.setMinutes(currentTime.getMinutes() + 60)
    lastRequestTimestamp.value = currentTime.getTime()
  }

  function signOut() {
    localStorage.removeItem('env')
    localStorage.removeItem('jwt')
    localStorage.removeItem('herokuToken')
    localStorage.removeItem('authJson')

    jwt.value = null
    herokuToken.value = null
    stopSystemStatusPolling()
  }

  async function resetSystem() {
    try {
      requesting.resetSystem = true
      await resetSystemToInitialState()
      stepper.value = null
    } finally {
      requesting.resetSystem = false
    }
  }

  // `afterSchemaChange`: this check follows a Field Settings save
  async function getSystemStatus({
    withLoader = true,
    afterSchemaChange = false,
  } = {}) {
    if (withLoader) {
      isLoadingSystemStatus.value = true
    }

    isSystemStatusError.value = false

    // A save always needs a reload
    if (afterSchemaChange) {
      reloadMetadataWhenReindexEnds()
    }

    // If this check fails, keep polling so the popup can't get stuck
    let isReindexing =
      !!pendingMetadataReload || !!systemStatus.value?.isCrawlerReindexing

    try {
      const miniStepper = await getMiniStepperStatus()
      isReindexing = miniStepper.isCrawlerReindexing

      if (isReindexing) {
        reloadMetadataWhenReindexEnds({ flushCache: afterSchemaChange })
      } else if (pendingMetadataReload) {
        // Reload first, so the popup only closes once the new fields are in
        const { flushCache } = pendingMetadataReload
        pendingMetadataReload = null
        await reloadMetadataAfterReindex({ flushCache })
      }

      systemStatus.value = miniStepper
      localStorage.setItem('systemStatus', JSON.stringify(miniStepper))
    } catch (error) {
      isSystemStatusError.value = true
      throw error
    } finally {
      isLoadingSystemStatus.value = false

      if (isReindexing) {
        scheduleSystemStatusCheck()
      }
    }
  }

  function scheduleSystemStatusCheck() {
    clearTimeout(systemStatusTimer)

    // No valid session: every check would fail
    if (isSessionExpired.value || !isUserLoggedIn.value) {
      return
    }

    systemStatusTimer = setTimeout(() => {
      // Failures are retried by the next check
      getSystemStatus({ withLoader: false }).catch(() => {})
    }, reindexingInterval)
  }

  function stopSystemStatusPolling() {
    clearTimeout(systemStatusTimer)
    systemStatusTimer = null
    pendingMetadataReload = null
  }

  // Owe a reload for when the reindex ends. Only the saving session flushes:
  // reads during a reindex can cache a stale field list for an hour.
  function reloadMetadataWhenReindexEnds({ flushCache = false } = {}) {
    pendingMetadataReload = {
      flushCache: flushCache || !!pendingMetadataReload?.flushCache,
    }
  }

  async function reloadMetadataAfterReindex({ flushCache }) {
    // Skip the SBC widget and broken connections, like the router guard does
    if (
      !usesSystemMetadata ||
      connectionsStore.isSomeConnectionDisconnected ||
      connectionsStore.isSomeConnectionMissing
    ) {
      return
    }

    // Not at setup: the stats store imports this store
    const statsStore = useStats()

    // Not the objects list: a save doesn't change it, and it breaks the wizard.
    // allSettled, so a failed reload can't keep the popup open.
    const results = await Promise.allSettled([
      getFieldsPerObjectMetadata({ flushCache }),
      getFieldsPerMultimapMetadata(),
      statsStore.getMetadata(),
    ])
    results
      .filter(result => result.status === 'rejected')
      .forEach(result =>
        console.error('Failed to reload metadata after reindex', result.reason),
      )
  }

  async function setSystemStatusLayoutsVisited() {
    await setMiniStepperStatusLayoutsVisited()
    systemStatus.value = {
      ...systemStatus.value,
      layoutsVisited: true,
    }
  }

  // `flushCache` is forwarded to the field metadata call only: it makes the
  // backend re-read the connector's schema instead of serving its cached copy,
  // which is what surfaces fields added to the Heroku mapping since last load.
  async function getSystemMetadata({ flushCache = false } = {}) {
    usesSystemMetadata = true
    isLoadingMetadata.value = true

    await Promise.all([
      getFieldsPerObjectMetadata({ flushCache }),
      getFieldsPerMultimapMetadata(),
      getFieldOperatorsMetadata(),
      getSemanticTypesMetadata(),
      getCountryStatesMetadata(),
    ]).finally(() => {
      isLoadingMetadata.value = false
    })
  }

  // Country -> states map for SF State & Country/Territory Picklists.
  // Kept in memory only (not localStorage).
  const countryStatesMap = ref(null)

  async function getCountryStatesMetadata() {
    if (!isSfCountryPicklistEnabled.value) {
      countryStatesMap.value = null
      return
    }

    try {
      countryStatesMap.value = await getCountryPicklistStates()
    } catch (error) {
      // Soft-fail: state editors degrade to "No data available" instead of
      // blocking the whole metadata load
      countryStatesMap.value = null
      console.error('Failed to load country states map', error)
    }
  }

  const isFieldOperatorsError = ref(false)
  const isFieldsPerObjectError = ref(false)
  const isFieldsPerMultimapError = ref(false)
  const isLoadingFieldsPerObject = ref(false)

  async function getFieldOperatorsMetadata() {
    try {
      isFieldOperatorsError.value = false
      const list = await getFieldOperators()
      operators.value = list
      localStorage.setItem('operators', JSON.stringify(list))
    } catch (error) {
      isFieldOperatorsError.value = true
      throw error
    }
  }

  async function getFieldsPerMultimapMetadata() {
    try {
      isFieldsPerMultimapError.value = false
      const list = await getFieldsPerMultiMap()
      fieldsPerMultiMap.value = list
      localStorage.setItem('fieldsPerMultiMap', JSON.stringify(list))
    } catch (error) {
      isFieldsPerMultimapError.value = true
      throw error
    }
  }

  async function getFieldsPerObjectMetadata({ flushCache, object } = {}) {
    try {
      isLoadingFieldsPerObject.value = true
      isFieldsPerObjectError.value = false
      const list = await getFields({ flushCache, object })
      if (object) {
        fieldsPerObject.value = {
          ...fieldsPerObject.value,
          ...list,
        }
      } else {
        fieldsPerObject.value = list
      }

      localStorage.setItem(
        'fieldsPerObject',
        JSON.stringify(fieldsPerObject.value),
      )
    } catch (error) {
      isFieldsPerObjectError.value = true
      throw error
    } finally {
      isLoadingFieldsPerObject.value = false
    }
  }

  const semanticTypes = ref([])
  const isSemanticTypesError = ref(false)
  async function getSemanticTypesMetadata() {
    try {
      isSemanticTypesError.value = false
      semanticTypes.value = await getSemanticTypes()
    } catch (error) {
      isSemanticTypesError.value = true
      throw error
    }
  }

  return {
    importUrl,
    getSystemMetadata,
    getFieldOperatorsMetadata,
    getFieldsPerMultimapMetadata,
    getFieldsPerObjectMetadata,
    getSemanticTypesMetadata,
    isFieldOperatorsError,
    isFieldsPerObjectError,
    isFieldsPerMultimapError,
    isLoadingFieldsPerObject,
    isStartingPageLoading,
    isLoadingMetadata,
    fieldsPerObject,
    fieldsPerMultiMap,
    operators,
    semanticTypes,
    enableXOBreakup,
    isAddressEnabled,
    isSlackIntegrationEnabled,
    isSfCountryPicklistEnabled,
    isDataIssuesEnabled,
    canSearchMerge,
    searchMergeFlagEnabled,
    countryStatesMap,
    dedupeAgentId,
    searchAgentId,
    searchMergeAgentId,
    refreshSearchMergeAgent,
    findFixAgentId,
    getStepper,
    packageInstallUrl,
    stepper,

    isCdLite,
    licenseUsage,

    contextMessage,
    requesting,
    isUserLoggedIn,
    isUserInactive,
    isSessionExpired,
    herokuToken,
    jwt,
    systemStatus,
    isLoadingSystemStatus,
    isSystemStatusError,
    user,
    isUserError,
    isUserFetched,
    roles,
    ensureRolesList,
    setContextMessage,
    getUser,
    getRolesList,
    setJWT,
    setHerokuToken,
    setSessionExpired,
    setLastRequestTimestamp,
    signOut,
    resetSystem,
    track,
    getSystemStatus,
    setSystemStatusLayoutsVisited,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSession, import.meta.hot))
}
